import { handleErrorDisplay, getCookieValue } from "kw/util/utils";
import { useState } from "react";
import api, { apiSettings } from ".";
import { identity } from "kanopi-components/utils/functional";

enum APIActions {
  Mutation,
  Query,
}

/**
 * Handle API calls in consistent way.
 * Set loading state
 * Errors are handled in index.js
 * @return loading, mutation, query
 */
export const useAPI = () => {
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState<boolean>();
  const executeInvoke = async (
    action: APIActions,
    query: unknown,
    callback: TCallbackFunction,
    onError: TCallbackFunction,
    onFinally: TCallbackFunction,
    params?: unknown
  ) => {
    const invoker = apiInvoker(action);
    setLoading(true);
    try {
      const response = await invoker(query, params);
      // Let the caller handle response as they need
      callback(response);
      setIsSuccess(true);
    } catch (error) {
      onError(error);
      setIsError(true);
    } finally {
      setLoading(false);
      onFinally();
    }
  };
  const mutation = async (
    query: unknown,
    callback: TCallbackFunction = identity,
    params?: unknown,
    onError: TCallbackFunction = defaultErrorHandler,
    onFinally: TCallbackFunction = () => undefined
  ) => {
    return executeInvoke(
      APIActions.Mutation,
      query,
      callback,
      onError,
      onFinally,
      params
    );
  };
  const query = async (
    query: unknown,
    callback: TCallbackFunction = identity,
    params?: unknown,
    onError: TCallbackFunction = defaultErrorHandler,
    onFinally: TCallbackFunction = () => undefined
  ) => {
    return executeInvoke(APIActions.Query, query, callback, onError, onFinally, params);
  };

  type fetchRequestArgs = {
    url: string;
    callback: TCallbackFunction;
    onError?: TCallbackFunction;
    onFinally?: TCallbackFunction;
    requestInit?: RequestInit;
  };

  const fetchRequest = ({
    url,
    callback,
    onError = defaultErrorHandler,
    onFinally = () => undefined,
    requestInit = {
      method: "GET",
    },
  }: fetchRequestArgs) => {
    requestInit["headers"] = requestInit?.headers || {};
    requestInit.headers["Authorization"] = apiSettings.headers.Authorization;
    requestInit.headers["X-CSRFToken"] = getCookieValue("csrftoken");
    setLoading(true);
    fetch(url, requestInit)
      .then((response) => {
        const isJson = response.headers
          .get("content-type")
          ?.includes("application/json");
        return isJson ? response.json() : response;
      })
      .then((result) => {
        return callback(result);
      })
      .catch((error) => {
        onError(error);
        setIsError(true);
      })
      .finally(() => {
        setLoading(false);
        onFinally();
      });
  };

  return { loading, isError, isSuccess, mutation, query, fetchRequest };
};

const defaultErrorHandler = (error) => {
  handleErrorDisplay(error);
};

const apiInvoker = (action: APIActions) => {
  if (action === APIActions.Mutation) {
    return api.mutation;
  } else {
    return api.query;
  }
};
